/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Button } from 'reactstrap';
import { onboardUser, rejectOnboardUser } from '@actions/user';
import { renderOutcome } from './OutcomeMessage';
import { Loader } from '@g17eco/atoms/loader';
import { Suggestion } from '@g17eco/types/search';
import ProfilePicture from '@features/profile/ProfilePicture';
import { OrganizationSearch } from '@features/search/OrganizationSearch';
import { OnboardingForm, OnboardingToken } from '@g17eco/types/onboarding';
import { authWithCredentials } from '@services/OktaClient';
import { BasicAlert } from '@g17eco/molecules/alert';

const ReactPasswordStrength = React.lazy(() => import('@g17eco/molecules/react-password-strength'));

enum Fields {
  firstName,
  lastName,
  jobTitle,
  phone,
  profilePicture,
  organization,
  country,
  email,
  password
}

enum FormMode {
  full,
  minimum
}

const requiredFields = {
  [FormMode.full]: [
    Fields.firstName,
    Fields.lastName,
    Fields.jobTitle,
    Fields.phone,
    Fields.profilePicture,
    Fields.organization,
    Fields.country,
    Fields.email,
    Fields.password
  ],
  [FormMode.minimum]: [
    Fields.firstName,
    Fields.lastName,
    Fields.email,
    Fields.password
  ]
}

interface OnboardingFormProps {
  token: OnboardingToken;
  countries: {
    name: string,
    code: string
  }[];
  reloadOnboarding: () => void;
  showLogin: (redirectPath?: string) => void;
  setUserMessage: (message?: string) => void;
}

class UserOnboarding extends React.Component<OnboardingFormProps & RouteComponentProps> {

  state = {
    form: {
      email: '',
      firstName: '',
      surname: '',
      password: '',
      rPassword: '',
      userAgreement: false,
      country: ''
    },
    showForm: false,
    isRejected: false,
    saving: false,
    error: false,
    errorMessage: '',
    mode: FormMode.minimum,
  };

  onboardUser = (data: OnboardingForm) => onboardUser(this.props.token, data);

  handleCompanySearch = (value: string, suggestion?: Suggestion) => {
    this.setState({
      form: {
        ...this.state.form,
        organizationId: suggestion ? suggestion._id : undefined,
        organizationName: suggestion ? suggestion.name : value,
      }
    })
  };

  addFiles = (files: any[]) => this.setState({ form: { ...this.state.form, files } });

  reject = () => {
    this.setState({ saving: true });
    this.props.setUserMessage('Rejecting...');

    rejectOnboardUser(this.props.token).then(result => {
      this.setState({ saving: false, isRejected: result.success });
      this.props.setUserMessage();
    });

    return false;
  };

  componentDidMount() {
    document.title = 'G17Eco - User Onboarding';
    const { token } = this.props;
    const { firstName, surname, email } = token.user;

    this.setState({
      form: {
        ...this.state.form,
        firstName: firstName,
        surname: surname,
        email: email,
        country: 'GB',
      }
    })
  }

  isFieldEnabled = (fieldName: Fields) => {
    const { mode } = this.state;
    return requiredFields[mode].includes(fieldName);
  };

  hideForm = (e: React.MouseEvent<any, MouseEvent>) => {
    e.preventDefault();
    this.setState({ showForm: false })
  }

  showForm = (e: React.MouseEvent<any, MouseEvent>) => {
    e.preventDefault();
    this.setState({ showForm: true })
  }

  handlePasswordChange = (state: { password: string }) => {
    this.setState({
      form: {
        ...this.state.form,
        password: state.password
      }
    })
  };

  handleChange = (e: React.FormEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const target = e.currentTarget;
    this.setState({
      form: {
        ...this.state.form,
        [target.name]: target.value
      }
    })
  };

  save = (e: React.FormEvent<HTMLFormElement>, form: OnboardingForm) => {
    e.preventDefault();
    this.setState({ saving: true, error: false });

    const { email: username, password } = form;

    this.onboardUser(form)
      .then(() => authWithCredentials({ username, password }))
      .then(() => this.props.reloadOnboarding())
      .catch((err) => {
        this.setState({
          saving: false,
          error: true,
          errorMessage: err.message
        });
      });
  }

  render() {
    const { form, saving, error, showForm, isRejected, errorMessage } = this.state;
    const { firstName, surname, email } = form;
    const { token } = this.props;

    const errorPasswordTooShort = form.password && form.password.length < 8;
    const errorPasswordMatch = !errorPasswordTooShort && form.password !== form.rPassword;
    const formDisabled = errorPasswordTooShort || errorPasswordMatch || saving;

    if (isRejected) {
      return renderOutcome({ title: 'Thank you for your reply' });
    }

    return <div className='userOnboarding row'>
      <div className='col offset-md-2 col-md-8'>
        <h1>Welcome!</h1>
        <p>You have been invited to join the {this.getName(token)} profile on the G17Eco sustainability platform.</p>
        <p>By joining you will support the collection or verification of data for measuring {this.getName(token, true)} sustainability efforts.</p>

        {!showForm && this.showDecisionButtons(saving)}

        {showForm &&
          <div className='row mt-5'>
            <div className='col offset-md-2 col-md-8'>
              <form onSubmit={(e) => this.save(e, form)}>

                {this.isFieldEnabled(Fields.firstName) && <>
                  <label htmlFor='firstName' className='strong mt-3'>First Name*</label>
                  <div className='input-group'>
                    <input type='text' required={true} className='form-control' name='firstName' defaultValue={firstName} onChange={this.handleChange} />
                  </div>
                </>}

                {this.isFieldEnabled(Fields.lastName) && <>
                  <label htmlFor='surname' className='strong mt-3'>Last Name*</label>
                  <div className='input-group'>
                    <input type='text' required={true} className='form-control' name='surname' defaultValue={surname} onChange={this.handleChange} />
                  </div>
                </>}

                {this.isFieldEnabled(Fields.jobTitle) && <>
                  <label htmlFor='jobTitle' className='strong mt-3'>Job Title*</label>
                  <div className='input-group'>
                    <input type='text' required={true} className='form-control' name='jobTitle' onChange={this.handleChange} />
                  </div>
                </>}

                {this.isFieldEnabled(Fields.phone) && <>
                  <label htmlFor='telephoneNumber' className='strong  mt-3'>Phone</label>
                  <div className='input-group'>
                    <input type='text' required={false} className='form-control'
                      name='telephoneNumber' onChange={this.handleChange} />
                  </div>
                </>}

                {this.isFieldEnabled(Fields.profilePicture) && <>
                  <ProfilePicture handleFilesAdded={this.addFiles} />
                </>}

                {this.isFieldEnabled(Fields.organization) && <>
                  <div className='input-group mt-3'>
                    <label htmlFor='organizationSearch' className='strong mt-3'>
                      Organization*
                  </label>
                    <OrganizationSearch
                      name={'organizationSearch'}
                      required={true}
                      inputClassName={'form-control'}
                      handleValueChange={this.handleCompanySearch} />
                  </div>
                </>}

                {this.isFieldEnabled(Fields.country) && <>
                  {this.countrySelect()}
                </>}

                {this.isFieldEnabled(Fields.email) && <>
                  <label htmlFor='email' className='strong mt-3'>E-mail</label>
                  <div className='input-group'>
                    <input type='text' required={true} className='form-control'
                      disabled={true} name='email' value={email}
                      onChange={this.handleChange} />
                  </div>
                </>}

                {this.isFieldEnabled(Fields.password) && <>
                  <label htmlFor='password' className='strong mt-3'>Password*</label>
                  <div className='input-group'>
                    <React.Suspense fallback={<Loader />}>
                      <ReactPasswordStrength
                        style={{ width: '100%' }}
                        required={true}
                        minScore={2}
                        changeCallback={this.handlePasswordChange}
                        inputProps={{ required: true, name: 'password', autoComplete: 'off', placeholder: 'Password' }}
                      />
                    </React.Suspense>
                  </div>

                  <label htmlFor='rPassword' className='strong mt-3'>Repeat Password*</label>
                  <div className='input-group'>
                    <input
                      required={true}
                      onChange={this.handleChange}
                      type='password'
                      name='rPassword'
                      placeholder='Confirm Password'
                      className='form-control'
                    />
                  </div>
                </>}

                {errorPasswordMatch &&
                  <div className='alert alert-danger mt-xs'>Please make sure both passwords match</div>
                }

                {errorPasswordTooShort &&
                  <div className='alert alert-danger mt-xs'> Password must be 8 characters long </div>
                }

                <label className='strong mt-3'>How my personal details are used</label>
                <div className='mt-3'>
                  To find out how we handle your data, please see our <a href='/legal-privacy-policy' target='_blank'>Privacy Policy</a>.
                </div>

                <div className='row mt-5'>
                  {error && <BasicAlert type={'danger'} className={'mt-xs'}>
                    {errorMessage || 'There was an error creating your account. Please try again.'}
                  </BasicAlert>}

                  <div className='col-3 pr-1'>
                    <Button
                      color='link-secondary'
                      onClick={this.hideForm}>
                      Back
                    </Button>
                  </div>

                  <div className='col-9'>
                    <Button
                      disabled={formDisabled}
                      type='submit'
                      className='w-100'
                      color='primary'>
                      {saving ? 'Saving...' : 'Create Account'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        }
      </div>
    </div>
  }

  showDecisionButtons(saving: boolean) {

    if (this.props.token.externalIdp) {
      return (
        <div>
          <Button className={'login-btn ml-2 mr-2'} onClick={() => this.props.showLogin()}>
            Please login with your SSO provider
          </Button>
        </div>
      )
    }

    return (
      <div className='mt-5 pr-1 text-right'>
        <Button
          type='button'
          onClick={this.showForm}
          name={'action'}
          value={'show'}
          color={'primary'}
          disabled={saving}
          className='w-50'>
          Create a new account</Button>

        <div>
          <Button color='link' size='sm' className='text-ThemeHeadingLight' onClick={() => this.props.showLogin()}>
            If you already have a login, <u>click here</u>
          </Button>
        </div>
      </div>
    );
  }

  getName(token: OnboardingToken, possessive = false) {
    if (!token || !token.initiativeName) {
      return '';
    }

    return <strong>{token.initiativeName}{possessive ? '\'s' : ''}</strong>;
  }

  countrySelect() {
    return (
      <div>
        <label htmlFor='country' className='strong mt-3'>Country*</label>
        <select
          value={this.state.form.country}
          onChange={(e) => this.handleChange(e)}
          name='country'
          className='form-control'>
          {this.props.countries.map(({ name, code }, i) => (
            <option key={`${i}_${code}`} value={code} label={name}>
              {name}
            </option>
          ))}
        </select>
      </div>
    )
  }
}

export default withRouter(UserOnboarding);
